import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cs from 'classnames';
import { formatAuCurrency } from 'shared/utils/currency';
import { useElementId } from 'shared/utils/elementIds';
import { Cart_ApplicationFragment, CartItemFragment, RateOptionFragment, useSetMinMonthsForEntireCartMutation, useSetMinMonthsForSingleItemMutation } from '../../generated.graphql';
 

interface Props {
  application: Cart_ApplicationFragment;
}


export const MinMonthsSelect = (props: Props) => {
  const { application } = props;
  const el = useElementId('MinMonthsSelect')
  const [result, setMinMonths] = useSetMinMonthsForEntireCartMutation()
  if (application.rateOptions.length > 1) {
    return (
      <>
        <div className="dropdown d-inline-block">
          <button 
            className="btn btn-link dropdown-toggle p-0" type="button" id={el} 
            style={{verticalAlign: 'baseline'}}
            disabled={result.fetching}
            data-toggle="dropdown" aria-haspopup="true" aria-expanded="false"
          >
            {application.minMonths === null && <>Select your minimum rental period</>}
            {application.rateOptions.map(o => o.minMonths === application.minMonths && (
              <MinRentalPeriod key={o.minMonths} minMonths={o.minMonths} highlight={application.portal.requireMinMonthsConfirmation}/>
            ))}
             <FontAwesomeIcon icon={faAngleDown} size={'2x'} className="ml-1" style={{verticalAlign: 'middle'}} />
          </button>
          <div className="dropdown-menu" aria-labelledby={el}>
            {application.rateOptions.map(o => (
              <button 
                type="button"
                key={o.minMonths}
                disabled={result.fetching}
                className={cs("dropdown-item", o.minMonths === application.minMonths && 'active')}
                onClick={() => {
                  setMinMonths({input: {portalSlug: application.portal.slug, minMonths: o.minMonths}})
                }}
              >
                <MinRentalPeriod minMonths={o.minMonths} highlight={false}/>{' '} 
                (${formatAuCurrency(o.rentalMonthly)} / month)
              </button>
            ))}
          </div>
        </div>

      </>
    )
  }
  return (
    <div className="d-inline-block">
      <span className="btn p-0" style={{verticalAlign: 'baseline', cursor: 'text', userSelect: 'auto'}}>
        {application.rateOptions.map(o => o.minMonths === application.minMonths && (
          <MinRentalPeriod key={o.minMonths} minMonths={o.minMonths} highlight={application.portal.requireMinMonthsConfirmation}/>
        ))}
      </span>
    </div>
  )
};



export const ChooseMinMonthsText = (props: Props) => (
  <div className="border-top py-4">
    <strong>Choose your minimum rental period</strong>
  </div>
);

export const CartLevelMinMonthsToggleButtons = (props: Props & {
  validate?: boolean
}) => {
  const { application } = props;
  const [result, setMinMonths] = useSetMinMonthsForEntireCartMutation()
  return (
      <MinMonthsToggleButtonGroup
        value={application.minMonths ?? null}
        validate={props.validate}
        rateOptions={application.rateOptions}
        disabled={result.fetching}
        onClick={(newMinMonths) => {
          setMinMonths({input: {portalSlug: application.portal.slug, minMonths: newMinMonths}})
        }}
      />
  );
};

export const ItemLevelMinMonthsToggleButtons = (props: {
  itemId: number
  rateOptions: ReadonlyArray<RateOptionFragment>
  portalSlug: string
  value: number | null
  validate?: boolean
  className?: string
}) => {
  const [result, setMinMonths] = useSetMinMonthsForSingleItemMutation()
  return (
      <MinMonthsToggleButtonGroup
        className={props.className}
        value={props.value ?? null}
        validate={props.validate}
        rateOptions={props.rateOptions}
        disabled={result.fetching}
        onClick={(newMinMonths) => {
          setMinMonths({input: {portalSlug: props.portalSlug, itemId: props.itemId, minMonths: newMinMonths}})
        }}
      />
  );
};

interface MinMonthsToggleButtonGroupProps {
  rateOptions: ReadonlyArray<RateOptionFragment>
  disabled: boolean
  value: number | null
  onClick: (minMonths: number) => void
  className?: string,
  validate?: boolean
}

export const MinMonthsToggleButtonGroup = (props: MinMonthsToggleButtonGroupProps) => {
  return (
      <div className={cs('btn-group', props.className)}>
        {props.rateOptions.map((o, i) => (
          <button 
            type="button"
            key={o.minMonths}
            disabled={props.disabled}
            className={cs(
              "btn btn-sm",
              props.validate === true && props.value === null ? "btn-outline-danger" : "btn-outline-dark", 
              o.minMonths === props.value && 'active',  //  bg-primary
              // i !== 0 && 'ml-2'
            )}
            onClick={() => props.onClick(o.minMonths)}
          >
            <RentalPeriodText minMonths={o.minMonths}/>
          </button>
        ))}
      </div>
  );
};



export const MinRentalPeriod = ({minMonths, highlight}: {minMonths: number, highlight: boolean}) => {
  const valueStr = `${minMonths} month${minMonths === 1 ? '' : 's'}`;
  return (
  <>Minimum rental period {highlight 
    ? <BorderedSpan><RentalPeriodText minMonths={minMonths}/></BorderedSpan> 
    : <RentalPeriodText minMonths={minMonths}/>
  }</>
)};

export const RentalPeriodText = ({minMonths}: {minMonths: number}) => {
  return <>{minMonths} month{minMonths === 1 ? '' : 's'}</>;
};


export const BorderedSpan = (props: React.PropsWithChildren<{}>) => (
  <span className='border border-dark rounded px-1'>{props.children}</span>
);